/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Checkbox, Col, Collapse, Form, Input, Radio, Row, Select, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { GraphContext } from '../../FlowGraph';
import { NODE_TYPES, SENDBOX_STATUSES } from '../../../../constants/flowGraph';
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';

// Constant declarations
const { Paragraph, Text } = Typography;

const SelectNodeForm = ({ form, selectedElement }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { elements } = useContext(GraphContext);
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();

  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  var childNodeIds = elements.filter(element => element.data && element.data.nodeType === 'edge' && element.source === selectedElement.id).map(edge => edge.target);
  var childNodes = elements.filter(element => element.data && element.data.nodeType === 'node' && childNodeIds.some(nodeId => nodeId === element.id));

  return (
    <Fragment>
      <LabelWithOriginalText label={t('Text')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />

      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            name={['data', 'label', lang]}
            hidden={currentBotLanguage !== lang}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Question text cannot be blank.') }]}
          >
            <RichTextEditor
              placeholder={t('Write some content')}
              includedVariableTypes={activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? [VARIABLE_TYPES.LIVECHAT] : []}
            />
          </Form.Item>
        ))
      }

      <Row>
        <Col span={24}>
          <Text>
            {t('Friendly name')}
          </Text>
        </Col>

        <Col span={24}>
          <Paragraph type="secondary">
            {t('A shorter version of the question text. It is used as the column name for this question, when exporting collected data.')}
          </Paragraph>
        </Col>
      </Row>

      <Form.Item
        name={['data', 'friendlyName']}
      >
        <Input
          placeholder={t('Enter a short friendly name')}
          size="large"
          maxLength={30}
        />
      </Form.Item>

      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <ArrowDownIcon rotate={isActive ? 180 : 0} />}
      >
        <Collapse.Panel
          header={t('Advanced Settings')}
          forceRender
        >
          <Form.Item
            name={['data', 'questionId']}
            label={<Text>
              {t('Variable name')}
              <Tooltip
                className="ml-1"
                placement="top"
                title={t('It is used for storing the answer to this question. Set a variable name to use the answer in messages templates, or in control flow nodes.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Text>}
            rules={[{
              pattern: /^[a-zA-Z0-9_$\\.]*$/,
              message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
            }]}
          >
            <Input
              placeholder={t('Enter the variable name')}
              size="large"
              maxLength={30}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>

          {
            childNodes.some(node => node.type === NODE_TYPES.TEXT_INPUT) ? (
              <Fragment>
                <LabelWithOriginalText label={t('Submit button label')} originalText={form.getFieldValue(['data', 'submitLabel', primaryBotLanguage])} />
                {
                  botLanguages.map(lang => (
                    <Form.Item
                      label=""
                      name={['data', 'submitLabel', lang]}
                      hidden={currentBotLanguage !== lang}
                    >
                      <Input
                        placeholder={t('Enter a submit label')}
                        size="large"
                      />
                    </Form.Item>
                  ))
                }
              </Fragment>
            ) : null
          }

          <Form.Item
            label=""
            name={['data', 'enableBackAction']}
            valuePropName="checked"
            className="mb-0"
          >
            <Checkbox>
              {t('Enable back button')}
            </Checkbox>
          </Form.Item>

          {
            childNodes.some(node => node.type === NODE_TYPES.MULTI_OPTION || node.type === NODE_TYPES.DYNAMIC_MULTI_OPTION) ? (
              <Fragment>
                <Form.Item
                  label={t('Render options as')}
                  name={['data', 'isDropdown']}
                  className="mt-3 mb-0"
                >
                  <Radio.Group>
                    <Radio
                      key={false}
                      value={false}
                      onClick={() => {
                        // TODO: Ensure this does not affect bot behavior after the multilang migration
                        form.setFieldValue(['data', 'submitLabel', currentBotLanguage], null);
                      }}
                    >
                      {t('Single Buttons')}
                    </Radio>

                    <Radio
                      key="card"
                      value="card"
                      disabled
                    >
                      {t('Card')}
                    </Radio>

                    <Radio
                      key={true}
                      value={true}
                    >
                      {t('Drop-down menu')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                {
                  form.getFieldValue(['data', 'isDropdown']) ? (
                    <Fragment>
                      <LabelWithOriginalText label={t('Submit button label')} originalText={form.getFieldValue(['data', 'submitLabel', primaryBotLanguage])} />
                      {
                        botLanguages.map(lang => (
                          <Form.Item
                            label=""
                            name={['data', 'submitLabel', lang]}
                            hidden={currentBotLanguage !== lang}
                            className="mt-3 mb-0"
                          >
                            <Input
                              placeholder={t('Enter a submit label')}
                              size="large"
                            />
                          </Form.Item>
                        ))
                      }
                    </Fragment>
                  ) : null
                }
              </Fragment>
            ) : childNodes.some(node => node.type === NODE_TYPES.LINK_OPTION || node.type === NODE_TYPES.OPTION) ? (
              <Fragment>
                <Form.Item
                  label={t('Render options as')}
                  name={['data', 'preferredRendering']}
                  className="mt-3 mb-0"
                  initialValue="buttons"
                >
                  <Radio.Group>
                    <Radio key="buttons" value="buttons">
                      {t('Single Buttons')}
                    </Radio>
                    <Radio key="card" value="card">
                      {t('Card')}
                    </Radio>
                    <Radio disabled>
                      {t('Drop-down menu')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Fragment>
            ) : (
              <Fragment>
                <Form.Item
                  label={t('Render options as')}
                  className="mt-3 mb-0"
                >
                  <Radio.Group disabled>
                    <Radio defaultChecked>
                      {t('Single Buttons')}
                    </Radio>
                    <Radio key={false} value={false}>
                      {t('Card')}
                    </Radio>
                    <Radio key={false} value={false}>
                      {t('Drop-down menu')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Fragment>
            )
          }

          <Form.Item
            label={t('Sendbox status')}
            name={['data', 'sendboxState']}
            className="mt-3 mb-0"
          >
            <Select
              placeholder={t('Choose the status of the sendbox')}
              size="large"
              getPopupContainer={triggerNode => triggerNode.parentElement}
              defaultValue={SENDBOX_STATUSES.DEFAULT}
              options={
                Object.entries(SENDBOX_STATUSES).map(([key, value]) => ({
                  label: t(key, { context: 'sendbox status' }),
                  value: value
                }))
              }
            />
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

SelectNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default SelectNodeForm;
